import {
    getEntityMasterTable,
    type MasterEntityType,
    type MasterTableColumnMeta,
} from '@api/entity-attributes';

export interface MasterRow {
    row: Record<string, unknown>;
    columns: MasterTableColumnMeta[];
}

/**
 * One entity's row as the master table returns it, custom attributes included.
 *
 * The record pages read their core fields and attributes from the same query the grid
 * runs, narrowed to one id, so a field reads the same on the page as in the grid and the
 * column metadata (which attributes exist, which core columns are editable) comes with it.
 * Null when the id is not this merchant's.
 */
export const loadMasterRow = async (entityType: MasterEntityType, id: string): Promise<MasterRow | null> => {
    const result = await getEntityMasterTable({ entityType, page: 1, pageSize: 1, filterEntityIds: [id] });
    const row = result.rows.find((r) => String(r._id ?? '') === id) ?? result.rows[0];
    return row ? { row, columns: result.columns } : null;
};

const MAPPING_PAGE = 200;

/**
 * The mapping rows of one item: a product's makers, or a material's suppliers.
 *
 * The mapping grids search by contains-match on both ids, so the result is narrowed to the
 * exact id here. The same rows the Vendor Mapping grids show, with the same dated price
 * columns, so the record and the grid never disagree about a price.
 */
export const loadMappingRows = async (
    entityType: 'VENDOR_PRODUCT_MAPPING' | 'VENDOR_MATERIAL_MAPPING',
    itemId: string,
): Promise<Record<string, unknown>[]> => {
    const key = entityType === 'VENDOR_PRODUCT_MAPPING' ? 'productId' : 'materialId';
    const result = await getEntityMasterTable({ entityType, page: 1, pageSize: MAPPING_PAGE, search: itemId });
    return result.rows.filter((r) => String(r[key] ?? '') === itemId);
};

/** A value off a master row, as a string, or null. */
export const cell = (row: Record<string, unknown> | null | undefined, key: string): string | null => {
    const v = row?.[key];
    return v == null ? null : String(v);
};
